import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { P11, Particles } from "./p11shared";
import { useP11Lang } from "./p11lang";
import { useP11Sound } from "./p11fx";

export default function P11Login() {
  const nav = useNavigate();
  const { lang, tt } = useP11Lang();
  const { play } = useP11Sound();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [needVerify, setNeedVerify] = useState(false);
  const [forgot, setForgot] = useState(false);

  const sendReset = async () => {
    if (!email) { toast.error(tt("email")); return; }
    try {
      await api.post("/project11/auth/forgot", { email });
      toast.success(tt("forgot_sent"));
      setForgot(false);
    } catch { toast.error("Mislukt"); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/project11/auth/login", { email, password });
      localStorage.setItem("p11_token", data.token);
      nav("/project11/dashboard", { replace: true });
    } catch (err) {
      play("error"); // direct foutgeluid bij mislukte login, samen met de melding
      const d = err?.response?.data?.detail;
      if (d === "EMAIL_NOT_VERIFIED") setNeedVerify(true);
      else toast.error(typeof d === "string" ? d : "Login mislukt");
    } finally { setBusy(false); }
  };

  const resend = async () => {
    try {
      await api.post("/project11/auth/resend-verification", { email });
      toast.success(tt("reg_done_body"));
      setNeedVerify(false);
    } catch { toast.error("Mislukt"); }
  };

  return (
    <div className="p11-root relative" data-testid="p11-login">
      <Particles density={40} />
      <div className="relative z-10 min-h-screen flex flex-col max-w-md mx-auto px-6 py-10">
        <div className="flex items-center justify-between p11-label">
          <Link to="/project11" className="hover:text-white transition-colors" data-testid="p11-login-back">{tt("back")}</Link>
          <span className="p11-label-yellow">PROJECT 11*</span>
        </div>
        <main className="flex-1 flex flex-col justify-center">
          <h1 className="p11-h2 text-3xl mb-10">{tt("login_title")}</h1>
          {needVerify ? (
            <div className="border p-6 p11-fade-in" style={{ borderColor: P11.yellow }} data-testid="p11-login-unverified">
              <p className="text-sm leading-relaxed" style={{ color: P11.gray2 }}>{tt("reg_done_body")}</p>
              <button onClick={resend} className="p11-btn-ghost mt-5" data-testid="p11-login-resend">{tt("send_email")}</button>
            </div>
          ) : (
            <div className="space-y-12">
              <div className="flex items-center justify-between p11-label">
                <span>{lang === "nl" ? "TOEGANG" : "ACCESS"}</span>
                <span>{lang === "nl" ? "BEVELIGD" : "SECURED"}</span>
              </div>
              <div>
                <label className="p11-label">{tt("email")}</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" className="p11-input mt-1 !text-xl" data-testid="p11-login-email" />
              </div>
              <div>
                <label className="p11-label">{tt("password")}</label>
                <div className="flex items-end gap-4 border-b transition-colors" style={{ borderColor: P11.border }}>
                  <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" onKeyDown={(e) => e.key === "Enter" && submit()} className="p11-input !border-b-0 mt-1 !text-xl flex-1" data-testid="p11-login-password" />
                  <button onClick={submit} disabled={busy || !email || !password} className="p11-h2 text-3xl pb-2 transition-colors disabled:opacity-30" style={{ color: P11.yellow }} aria-label="Log in" data-testid="p11-login-submit">→</button>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <button onClick={() => setForgot(!forgot)} className="p11-label hover:text-white transition-colors" data-testid="p11-login-forgot">{tt("forgot")}</button>
                <Link to="/project11/register" className="p11-label hover:text-white transition-colors border-b border-transparent hover:border-[#858585] pb-0.5" data-testid="p11-login-register">{tt("become")}</Link>
              </div>
              {forgot && (
                <div className="border p-5 p11-fade-in" style={{ borderColor: P11.border }} data-testid="p11-forgot-panel">
                  <p className="p11-label mb-3">{tt("forgot_sent")}</p>
                  <button onClick={sendReset} className="p11-btn-ghost" data-testid="p11-forgot-send">{tt("send_email")}</button>
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
